import { Mine__factory } from 'public/abi/evm/types';
import type { ActivityRecord, TActivitiesResponse } from '~/types/apiService';
import { BigNumber } from 'bignumber.js';
import { type BigNumberish, formatEther } from 'ethers';
import useEnvs from '~/composables/useEnvs';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import type { MiningsTableRow } from '~/utils/constants';
import useTotalMined from '~/composables/useTotalMined';

interface MiningsTableData {
  tableData: Ref<MiningsTableRow[], MiningsTableRow[]>;
  updateTableData: () => [] | void;
  isLoading: Ref<boolean>;
}

// TODO: Refactor this to be more efficient and easier to understand, it is too complex and hard to maintain.
// consider to provide appropriate data from contract/api server or restructure the table entirely
export default (isSecondaryToken: boolean = false): MiningsTableData => {
  const { blockchain } = useEnvs();
  const { address } = useWeb3ModalAccount();
  const { minedBalance } = useMinedBalance();
  const { totalMinedAmount, isLoading: totalMinedLoading, secondaryTokenAmounts } = useTotalMined();
  const store = useMainStore();
  const { data: activityList } = useNuxtData<TActivitiesResponse>('/v2/activity');
  const { getProvider } = useProvider();

  const tableData = ref([] as MiningsTableRow[]);
  const licensesContractData = ref<[bigint[], bigint[], bigint] | null>(null);
  const isLoading = ref(false);

  const currentContractAddress = computed(() => blockchain.contracts[store.currentToken].addresses.contract);

  const secondaryTokenAddress = computed(() => {
    return blockchain.contracts[store.currentToken].addresses.secondaryToken;
  });

  watch(
    [address, () => store.currentToken, () => activityList.value],
    async () => {
      const isMine = blockchain.contracts?.[store.currentToken]?.addresses.mine;

      if (!isMine || !address.value) return;

      isLoading.value = true;

      const mineAddress = blockchain.contracts[store.currentToken].addresses.mine;

      if (!mineAddress) throw new Error('No mine address found');

      const mineContract = Mine__factory.connect(mineAddress, getProvider());

      licensesContractData.value = await mineContract.getLicenses(address.value, 0, 255);

      updateTableData();

      isLoading.value = false;
    },
    {
      immediate: true,
      deep: true
    }
  );

  function updateTableData() {
    // Get all purchases (both primary and secondary) to create proper mapping
    const allPurchases = activityList.value?.items
      .filter((listItem: ActivityRecord) => {
        const isPrimaryTokenAddress = listItem.tokenAddress === currentContractAddress.value;
        const isSecondaryTokenAddress = listItem.tokenAddress === secondaryTokenAddress.value;

        const isPurchase = listItem.type === 'PURCHASE';
        const isSpecialLock = listItem.type === 'SPECIAL_LOCK';

        return (isPurchase || isSpecialLock) && (isPrimaryTokenAddress || isSecondaryTokenAddress);
      })
      .reverse();

    if (!licensesContractData.value || !allPurchases?.length) {
      tableData.value = [];
      return;
    }

    const PRIMARY_TOKEN_LICENSE_INDEX = 0;
    const SECONDARY_TOKEN_LICENSE_INDEX = 1;
    const PRIMARY_TOKEN_MINED_BALANCE_INDEX = 0;
    const SECONDARY_TOKEN_MINED_BALANCE_INDEX = 1;

    const licensesAmountsArr: BigNumberish[] =
      licensesContractData.value[isSecondaryToken ? SECONDARY_TOKEN_LICENSE_INDEX : PRIMARY_TOKEN_LICENSE_INDEX];

    // Create a mapping of license index to purchase data
    // Since licenses are created in order, we can map by index
    const licenseToPurchaseMap = new Map();
    allPurchases
      .filter(
        (p) =>
          p.tokenAddress.toLowerCase() ===
          (isSecondaryToken ? secondaryTokenAddress.value?.toLowerCase() : currentContractAddress.value?.toLowerCase())
      )
      .forEach((purchase, index) => {
        licenseToPurchaseMap.set(index, purchase);
      });

    const result: any[] = [];
    const CLAIMED_TEXT = 'Claimed';

    // Get available balance for claiming
    const availableBalance = BigNumber(
      formatEther(
        minedBalance.value?.[
          isSecondaryToken ? SECONDARY_TOKEN_MINED_BALANCE_INDEX : PRIMARY_TOKEN_MINED_BALANCE_INDEX
        ] || 0
      )
    );

    // Get total mined amount to know which licenses have been processed
    let totalMined: BigNumber;

    if (isSecondaryToken) {
      // For secondary token: use the exact same formula as dashboard.vue
      const secondaryTokenAmountsSum =
        secondaryTokenAmounts.value?.reduce(
          (sum, current) => BigNumber(sum).plus(new BigNumber(current).shiftedBy(-18)).toNumber(),
          0
        ) || 0;

      const currentSecondaryTokenBalance = minedBalance.value?.[3];
      const secondaryTokenTotalProduced = BigNumber(secondaryTokenAmountsSum)
        .minus(formatEther(currentSecondaryTokenBalance || 0))
        .toString();

      totalMined = BigNumber(secondaryTokenTotalProduced);
    } else {
      totalMined = BigNumber(totalMinedAmount.primaryToken);
    }

    // Calculate how much has already been claimed
    // If totalMined is 0 or less than availableBalance, then nothing has been claimed yet
    const alreadyClaimed = totalMined.isGreaterThan(availableBalance)
      ? totalMined.minus(availableBalance)
      : BigNumber(0);

    if (totalMinedLoading.value) {
      tableData.value = [];
      return;
    }

    //if (totalMined.isZero() && availableBalance.isZero()) {
    //  tableData.value = [];
    //  return;
    //}

    // Track how much has been claimed so far
    let claimedSoFar = BigNumber(0);

    // First, collect all valid licenses with their data
    const validLicenses: Array<{
      amount: BigNumber;
      originalIndex: number;
      purchase: any;
    }> = [];

    licensesAmountsArr.forEach((licenseAmount: any, originalIndex: number) => {
      if (!new BigNumber(String(licenseAmount)).gt(0)) {
        return;
      }

      const licenseAmountBN = BigNumber(formatEther(licenseAmount));

      if (isSecondaryToken && !licenseAmountBN.isGreaterThan(0)) {
        return;
      }

      const correspondingPurchase = licenseToPurchaseMap.get(originalIndex);
      validLicenses.push({
        amount: licenseAmountBN,
        originalIndex,
        purchase: correspondingPurchase
      });
    });

    // Sort licenses by creation time (oldest first) to determine processing order
    const sortedByOldest = [...validLicenses].sort((a, b) => {
      const timeA = new Date(a.purchase?.blockTimestamp || 0).getTime();
      const timeB = new Date(b.purchase?.blockTimestamp || 0).getTime();
      return timeA - timeB; // Oldest first
    });

    // Determine the status of each license
    const licenseStatuses = new Map<
      number,
      {
        isFullyClaimed: boolean;
        isPartiallyClaimed: boolean;
        claimedAmount: BigNumber;
        availableAmount: BigNumber;
        transactionHash: string;
      }
    >();

    sortedByOldest.forEach((license) => {
      const { amount: licenseAmount, originalIndex, purchase } = license;

      if (!purchase) {
        return;
      }

      // Check how much of this license has been claimed
      const nextClaimedAmount = claimedSoFar.plus(licenseAmount);

      if (nextClaimedAmount.isLessThanOrEqualTo(alreadyClaimed)) {
        // This entire license has been claimed
        licenseStatuses.set(originalIndex, {
          isFullyClaimed: true,
          isPartiallyClaimed: false,
          claimedAmount: licenseAmount,
          availableAmount: BigNumber(0),
          transactionHash: purchase.transactionHash
        });
        claimedSoFar = nextClaimedAmount;
      } else if (claimedSoFar.isLessThan(alreadyClaimed)) {
        // This license is partially claimed
        const claimedFromThisLicense = alreadyClaimed.minus(claimedSoFar);
        const availableFromThisLicense = licenseAmount.minus(claimedFromThisLicense);

        licenseStatuses.set(originalIndex, {
          isFullyClaimed: false,
          isPartiallyClaimed: false,
          claimedAmount: BigNumber(0),
          availableAmount: availableFromThisLicense,
          transactionHash: purchase.transactionHash
        });
        claimedSoFar = alreadyClaimed;
      } else {
        // This license is not claimed yet - it should show the minedBalance
        // But we need to distribute the minedBalance among unclaimed licenses
        licenseStatuses.set(originalIndex, {
          isFullyClaimed: false,
          isPartiallyClaimed: false,
          claimedAmount: BigNumber(0),
          availableAmount: BigNumber(0),
          transactionHash: purchase.transactionHash
        });
      }
    });

    // Now distribute the minedBalance among licenses that have been processed but not fully claimed
    // The minedBalance should be distributed from oldest to newest
    let remainingMinedBalance = availableBalance;

    // Process licenses from oldest to newest to distribute minedBalance
    [...sortedByOldest].reverse().forEach((license, index) => {
      const { originalIndex } = license;
      const status = licenseStatuses.get(index);

      if (status && !status.isFullyClaimed && remainingMinedBalance.isGreaterThan(0)) {
        if (status.availableAmount.isGreaterThan(0)) {
          // This license is partially claimed, fill it first
          const unclaimedPortion = status.availableAmount;
          if (remainingMinedBalance.isGreaterThanOrEqualTo(unclaimedPortion)) {
            // Fill the unclaimed portion completely
            status.availableAmount = unclaimedPortion;
            remainingMinedBalance = remainingMinedBalance.minus(unclaimedPortion);
          } else {
            // Fill with remaining minedBalance
            status.availableAmount = new BigNumber(remainingMinedBalance);
            remainingMinedBalance = BigNumber(0);
          }
        } else {
          // This license is not claimed yet, fill it with remaining minedBalance
          const licenseAmount = BigNumber(formatEther(licensesAmountsArr[originalIndex]));
          if (remainingMinedBalance.isGreaterThanOrEqualTo(licenseAmount)) {
            status.availableAmount = licenseAmount;
            remainingMinedBalance = remainingMinedBalance.minus(licenseAmount);
          } else if (remainingMinedBalance.isGreaterThan(0)) {
            status.availableAmount = remainingMinedBalance;
            remainingMinedBalance = BigNumber(0);
          }
        }
      }
    });

    // Generate result using the license statuses
    validLicenses.forEach((license) => {
      const { amount: licenseAmount, purchase } = license;

      if (!purchase) {
        return;
      }

      const status = Array.from(licenseStatuses.values()).find((l) => l.transactionHash === purchase.transactionHash);

      if (!status) {
        // License not processed yet
        result.push({
          amount: licenseAmount.toString(),
          createdAt: purchase.blockTimestamp,
          available: '0'
        });
        return;
      }

      if (status.isFullyClaimed) {
        // License is fully claimed
        result.push({
          amount: licenseAmount.toString(),
          createdAt: purchase.blockTimestamp,
          available: CLAIMED_TEXT
        });
      } else if (status.isPartiallyClaimed) {
        // License is partially claimed (shouldn't happen with current logic, but keeping for safety)
        result.push({
          amount: licenseAmount.toString(),
          createdAt: purchase.blockTimestamp,
          available: CLAIMED_TEXT
        });
      } else if (status.availableAmount.isGreaterThan(0) && availableBalance.isGreaterThan(0)) {
        // License has available amount for claiming, but only if there's actually minedBalance available
        result.push({
          amount: licenseAmount.toString(),
          createdAt: purchase.blockTimestamp,
          available: status.availableAmount.decimalPlaces(2).toString()
        });
      } else {
        // License is not processed yet
        result.push({
          amount: licenseAmount.toString(),
          createdAt: purchase.blockTimestamp,
          available: '0'
        });
      }
    });

    tableData.value = result.reverse();
  }

  watchEffect(async () => {
    updateTableData();
  });

  watch(
    [() => minedBalance.value, () => totalMinedAmount],
    async () => {
      updateTableData();
    },
    { deep: true }
  );

  return {
    tableData,
    updateTableData,
    isLoading
  };
};
